import React, { useState, useEffect } from 'react';
import { Heart, Trash2, CheckSquare, Square, XSquare, Loader2 } from 'lucide-react';
import authService from '../services/AuthService';
import { API_BASE, getSupplierLogo } from '../consts';
import DealCard from '../components/DealCard';
import MinimizedDealCard from '../components/MinimizedDealCard';
import AdvancedFilters from '../components/AdvancedFilters';
import { calculateReferralFee, calculateWFSFee } from '../utils';

const INITIAL_FILTERS = {
    marketplace: [],
    min_roi: -1,
    max_roi: -1,
    min_profit: -1,
    max_profit: -1,
    profitable_only: false,
    search_text: '',
    min_cost: -1,
    max_cost: -1,
    min_sell_price: -1,
    max_sell_price: -1,
    coupon_amount: 0,
    days_since_found: -1,
    amazon: {
        exclude_amazon_selling: false,
        exclude_walmart_3p: false,
        no_variations: false,
        no_private_label: false,
        min_bsr: -1,
        max_bsr: -1,
        min_bsr_percent: -1,
        max_bsr_percent: -1,
        min_offers: -1,
        max_offers: -1,
        min_bsr_drops_30: -1,
        min_bsr_drops_90: -1,
        min_est_sales: -1,
        max_weight: -1,
        gated_status: 'all',
        fulfillment: 'fba'
    },
    walmart: {
        exclude_walmart_selling: false,
        exclude_walmart_3p: false,
        fulfillment: 'wfs',
        min_offers: -1,
        max_offers: -1,
        in_stock_only: false,
        min_reviews: -1,
        min_rating: -1,
        selected_badges: []
    },
    ebay: {
        exclude_walmart_3p: false,
        min_offers: -1,
        max_offers: -1,
        min_est_sales: -1
    },
    suppliers: {
        selected_suppliers: [],
        banned_suppliers: []
    },
    shared: {
        selected_categories: []
    }
};

const exportToCSV = (filteredDeals, filters) => {
    const deals = filteredDeals.map(g => g.primaryDeal);

    if (deals.length === 0) {
        alert('No deals to export!');
        return;
    }

    const headers = [
        'Title', 'Supplier', 'Source Price', 'Coupon/Discount', 'Effective Cost',
        'Marketplace', 'Sell Price', 'Profit', 'ROI (%)', 'Total Fees',
        'FBA/WFS Fee', 'Referral Fee', 'Placement Fees', 'Inbound Shipping',
        'Sales Tax', 'Packaging Cost', 'Other Costs', 'ASIN/Item ID', 'UPC',
        'Brand', 'Category', 'BSR', 'BSR %', 'BSR Avg 30d', 'BSR Avg 90d',
        'Offers', 'Variations', 'BSR Drops 30d', 'BSR Drops 90d', 'Est. Sales',
        'Keepa Monthly Sold', 'Gated Status', 'Amazon Sells', 'Walmart Sells',
        'In Stock', 'Reviews', 'Rating', 'Weight', 'Product URL', 'Marketplace URL'
    ];

    const getFeeSettings = (marketplace) => {
        const storageKey = `${marketplace}_fee_settings`;
        const saved = localStorage.getItem(storageKey);
        return saved ? JSON.parse(saved) : {};
    };

    const rows = deals.map(deal => {
        const data = deal.marketplace_data || {};
        const feeSettings = getFeeSettings(deal.marketplace);
        const couponPercent = filters.coupon_amount || 0;
        const couponAmount = (deal.source_price || 0) * (couponPercent / 100);
        const effectiveCost = (deal.source_price || 0) - couponAmount;
        const itemWeight = data.weight || deal.weight || 1;

        let sellPrice = 0, fbaWfsFee = 0, refFee = 0, placementFees = 0, inboundShipping = 0;
        let salesTax = effectiveCost * ((feeSettings.salesTax || 0) / 100);
        let packagingCost = feeSettings.packagingCost || 0;
        let otherCosts = feeSettings.otherCosts || 0;

        if (deal.marketplace === 'amazon') {
            sellPrice = data.az_price || 0;
            fbaWfsFee = filters.amazon.fulfillment === 'fba' ? (data.fba_fee || 0) : 0;
            refFee = sellPrice * (data.ref_fee_perc || 0.15);
            placementFees = filters.amazon.fulfillment === 'fba' ? (feeSettings.placementFees || 0) : 0;
            inboundShipping = filters.amazon.fulfillment === 'fba' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
        } else if (deal.marketplace === 'walmart') {
            sellPrice = data.price_value || 0;
            fbaWfsFee = filters.walmart.fulfillment === 'wfs' ? (
                (data.dimensions && itemWeight) ? calculateWFSFee(data.dimensions, itemWeight, {
                    isApparel: data.category?.toLowerCase().includes("clothing") || data.category?.toLowerCase().includes("apparel"),
                    retailPrice: sellPrice
                }) : 3.45
            ) : 0;
            refFee = calculateReferralFee(sellPrice, data.category, data.sub_category);
            inboundShipping = filters.walmart.fulfillment === 'wfs' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
        } else if (deal.marketplace === 'ebay') {
            sellPrice = data.lowest_price || 0;
            const totalSale = sellPrice;
            let finalValueFee = totalSale <= 7500 ? totalSale * 0.136 : (7500 * 0.136) + ((totalSale - 7500) * 0.0235);
            const storeDiscount = { none: 0, basic: 0.066, premium: 0.088, anchor: 0.102, enterprise: 0.110 }[feeSettings.storeSubscription || 'none'] || 0;
            const topRatedDiscount = feeSettings.topRated ? 0.10 : 0;
            finalValueFee = finalValueFee * (1 - storeDiscount - topRatedDiscount);
            fbaWfsFee = finalValueFee;
            refFee = (totalSale * 0.0235) + 0.30;
        }

        const totalFees = fbaWfsFee + refFee + placementFees + inboundShipping + salesTax + packagingCost + otherCosts;
        const profit = sellPrice - effectiveCost - totalFees;
        const roi = effectiveCost > 0 ? ((profit / effectiveCost) * 100) : 0;

        const escapeCSV = (val) => {
            const str = String(val ?? '');
            return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
        };

        return [
            escapeCSV(deal.title), escapeCSV(deal.supplier_name), (deal.source_price || 0).toFixed(2),
            couponPercent > 0 ? `${couponPercent}%` : '0', effectiveCost.toFixed(2),
            deal.marketplace, sellPrice.toFixed(2), profit.toFixed(2), roi.toFixed(1),
            totalFees.toFixed(2), fbaWfsFee.toFixed(2), refFee.toFixed(2), placementFees.toFixed(2),
            inboundShipping.toFixed(2), salesTax.toFixed(2), packagingCost.toFixed(2), otherCosts.toFixed(2),
            data.asin || data.item_id || '', deal.upc || '', escapeCSV(data.brand || ''),
            escapeCSV(data.category || ''), data.sales_rank || '', data.sales_rank_perc || '',
            data.sales_rank_avg_30_days || '', data.sales_rank_avg_90_days || '',
            data.total_offers_count || data.offers || data.total_offers || '',
            data.variations || '', data.sales_rank_drops_30_days || '', data.sales_rank_drops_90_days || '',
            data.estimate_sales_30_days || data.estimated_monthly_sales || '',
            data.keepa_monthly_sold || '', deal.gated_status || '',
            data.az_sells_it ? 'Yes' : 'No', data.is_walmart ? 'Yes' : 'No',
            data.in_stock !== undefined ? (data.in_stock ? 'Yes' : 'No') : '',
            data.number_of_reviews || '', data.average_rating || '', data.weight || deal.weight || '',
            deal.product_url || '', data.az_link || data.walmart_link || data.ebay_search_link || ''
        ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `favorites_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
};

const Favorites = ({ user }) => {
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [filteredDeals, setFilteredDeals] = useState([]);
    const [settingsUpdateTrigger, setSettingsUpdateTrigger] = useState(0);
    const [afRender, setAFRender] = useState(0);

    const [isCompactView, setIsCompactView] = useState(() => {
        const stored = localStorage.getItem('compact_deal_view');
        return stored === null ? true : stored === 'true';
    });

    const [bulkMode, setBulkMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const amazonLinked = user?.amazon_linked_account && Object.keys(user.amazon_linked_account).length > 0;

    useEffect(() => {
        loadFavorites();
    }, []);

    const loadFavorites = async () => {
        setLoading(true);
        try {
            const response = await authService.authenticatedFetch(`${API_BASE}/api/favorites`);
            const data = await response.json();
            setFavorites(data.favorites || []);
        } catch (error) {
            console.error('Failed to load favorites:', error);
        } finally {
            setLoading(false);
        }
    };

    const deals = favorites.map(f => f.deal_data);

    const getFeeSettings = (marketplace) => {
        const storageKey = `${marketplace}_fee_settings`;
        const saved = localStorage.getItem(storageKey);
        return saved ? JSON.parse(saved) : {};
    };

    const getAllCategories = () => {
        const categorySet = new Set();
        deals.forEach(deal => {
            const category = (deal.marketplace_data || {}).category;
            if (category && category.trim()) categorySet.add(category.trim());
        });
        return Array.from(categorySet).sort();
    };

    const getAllWalmartBadges = () => {
        const badgeSet = new Set();
        deals.forEach(deal => {
            if (deal.marketplace === 'walmart' && deal.marketplace_data?.badges) {
                deal.marketplace_data.badges.forEach(badge => {
                    if (badge.text) badgeSet.add(badge.text);
                });
            }
        });
        return Array.from(badgeSet).sort();
    };

    const getAllSuppliers = () => {
        const supplierMap = new Map();
        deals.forEach(deal => {
            const name = deal.supplier_name;
            if (name && !supplierMap.has(name)) {
                supplierMap.set(name, { name, logo: getSupplierLogo(name) });
            }
        });
        return Array.from(supplierMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    };

    // Filter deals - same logic as DealsViewer
    useEffect(() => {
        let result = [...deals];

        if (filters.days_since_found > 0) {
            const cutoff = new Date();
            cutoff.setDate(cutoff.getDate() - filters.days_since_found);
            result = result.filter(deal => {
                const dateStr = deal.created_at;
                try {
                    const d = new Date(dateStr.replace(' ', 'T') + (/(Z|[+-]\d{2}:\d{2})$/.test(dateStr) ? '' : 'Z'));
                    return d >= cutoff;
                } catch { return true; }
            });
        }

        // Marketplace filter
        if (filters.marketplace.length > 0) {
            result = result.filter(deal => filters.marketplace.includes(deal.marketplace));
        }

        if (filters.search_text) {
            const searchLower = filters.search_text.toLowerCase();
            result = result.filter(deal => {
                const title = (deal.title || '').toLowerCase();
                const brand = (deal.marketplace_data?.brand || '').toLowerCase();
                const asin = (deal.marketplace_data?.asin || '').toLowerCase();
                const itemId = (deal.marketplace_data?.item_id || '').toLowerCase();
                const upc = (deal.upc || '').toLowerCase();
                return title.includes(searchLower) || brand.includes(searchLower) ||
                    asin.includes(searchLower) || itemId.includes(searchLower) || upc.includes(searchLower);
            });
        }

        const couponPercent = filters.coupon_amount || 0;

        if (filters.min_cost > 0) {
            result = result.filter(deal => {
                const effectiveCost = (deal.source_price || 0) * (1 - couponPercent / 100);
                return effectiveCost >= filters.min_cost;
            });
        }

        if (filters.max_cost > 0) {
            result = result.filter(deal => {
                const effectiveCost = (deal.source_price || 0) * (1 - couponPercent / 100);
                return effectiveCost <= filters.max_cost;
            });
        }

        result = result.filter(deal => {
            const couponAmount = (deal.source_price || 0) * (couponPercent / 100);
            const sourcePrice = (deal.source_price || 0) - couponAmount;
            const data = deal.marketplace_data || {};
            const feeSettings = getFeeSettings(deal.marketplace);
            const itemWeight = data.weight || deal.weight || 1;

            let sellPrice = 0, profit = 0, roi = 0;

            const bannedSuppliers = filters.suppliers.banned_suppliers || [];
            if (bannedSuppliers.length > 0) {
                const supplierName = (deal.supplier_name || '').toLowerCase();
                if (bannedSuppliers.some(banned => banned.toLowerCase() === supplierName)) return false;
            }

            if (filters.suppliers.selected_suppliers.length > 0) {
                const supplierName = (deal.supplier_name || '').toLowerCase();
                const isSelected = filters.suppliers.selected_suppliers.some(
                    selected => selected.toLowerCase() === supplierName.toLowerCase()
                );
                if (!isSelected) return false;
            }

            if (deal.marketplace === 'amazon') {
                sellPrice = data.az_price || 0;
                const fbaFee = filters.amazon.fulfillment === 'fba' ? (data.fba_fee || 0) : 0;
                const refFeePerc = data.ref_fee_perc || 0.15;
                const refFee = sellPrice * refFeePerc;
                const placementFees = filters.amazon.fulfillment === 'fba' ? (feeSettings.placementFees || 0) : 0;
                const inboundShipping = filters.amazon.fulfillment === 'fba' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
                const salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
                const otherCosts = feeSettings.otherCosts || 0;
                const totalFees = fbaFee + refFee + placementFees + inboundShipping + salesTax + otherCosts;
                profit = sellPrice - sourcePrice - totalFees;
                roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

                if (filters.amazon.exclude_amazon_selling && data.az_sells_it) return false;
                if (filters.amazon.no_variations && (data.variations || 0) > 1) return false;
                if (filters.amazon.no_private_label && (data.total_offers_count || 0) < 4) return false;

                if (filters.amazon.exclude_walmart_3p) {
                    const supplierLower = (deal.supplier_name || '').toLowerCase();
                    const titleLower = (deal.title || '').toLowerCase();
                    const hasWalmartSeller = titleLower.includes('(seller: walmart.com)') || titleLower.includes('(seller: walmart)');
                    if (supplierLower === 'walmart' && !hasWalmartSeller) return false;
                }

                const excludedBrands = JSON.parse(localStorage.getItem('excluded_brands') || '[]');
                const brand = (data.brand || '').toLowerCase();
                if (excludedBrands.some(b => brand === b.toLowerCase())) return false;

                if (filters.shared.selected_categories.length > 0) {
                    const dealCategory = data.category || '';
                    if (!filters.shared.selected_categories.includes(dealCategory)) return false;
                }

                if (filters.amazon.min_bsr > 0 && (data.sales_rank || 0) < filters.amazon.min_bsr) return false;
                if (filters.amazon.max_bsr > 0 && ((data.sales_rank || 0) > filters.amazon.max_bsr || (data.sales_rank || 0) <= 0)) return false;
                if (filters.amazon.min_bsr_percent > 0 && (data.sales_rank_perc || 0) < filters.amazon.min_bsr_percent) return false;
                if (filters.amazon.max_bsr_percent > 0 && ((data.sales_rank_perc || 0) > filters.amazon.max_bsr_percent || (data.sales_rank_perc || 0) <= 0)) return false;
                if (filters.amazon.min_offers >= 0 && (data.total_offers_count || 0) < filters.amazon.min_offers) return false;
                if (filters.amazon.max_offers >= 0 && (data.total_offers_count || 0) > filters.amazon.max_offers) return false;
                if (filters.amazon.min_bsr_drops_30 > 0 && (data.sales_rank_drops_30_days || 0) < filters.amazon.min_bsr_drops_30) return false;
                if (filters.amazon.min_bsr_drops_90 > 0 && (data.sales_rank_drops_90_days || 0) < filters.amazon.min_bsr_drops_90) return false;

                const estSales = data.keepa_monthly_sold || data.estimate_sales_30_days || 0;
                if (filters.amazon.min_est_sales > 0 && estSales < filters.amazon.min_est_sales) return false;
                if (filters.amazon.max_weight > 0 && (data.weight || 0) > filters.amazon.max_weight) return false;

                if (filters.amazon.gated_status !== 'all') {
                    if (filters.amazon.gated_status === 'not_gated') {
                        if (deal.gated_status === 'gated') return false;
                    } else {
                        if (deal.gated_status !== filters.amazon.gated_status) return false;
                    }
                }

            } else if (deal.marketplace === 'walmart') {
                sellPrice = data.price_value || 0;
                const wfsFee = filters.walmart.fulfillment === 'wfs' ? (
                    (data.dimensions && itemWeight) ? calculateWFSFee(data.dimensions, itemWeight, {
                        isApparel: data.category?.toLowerCase().includes("clothing") || data.category?.toLowerCase().includes("apparel"),
                        retailPrice: sellPrice
                    }) : 3.45
                ) : 0;
                const refFee = calculateReferralFee(sellPrice, data.category, data.sub_category);
                const inboundShipping = filters.walmart.fulfillment === 'wfs' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
                const salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
                const otherCosts = feeSettings.otherCosts || 0;
                const totalFees = wfsFee + refFee + inboundShipping + salesTax + otherCosts;
                profit = sellPrice - sourcePrice - totalFees;
                roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

                if (filters.walmart.exclude_walmart_selling && data.is_walmart) return false;
                if (filters.walmart.in_stock_only && !data.in_stock) return false;

                if (filters.walmart.exclude_walmart_3p) {
                    const supplierLower = (deal.supplier_name || '').toLowerCase();
                    const titleLower = (deal.title || '').toLowerCase();
                    const hasWalmartSeller = titleLower.includes('(seller: walmart.com)') || titleLower.includes('(seller: walmart)');
                    if (supplierLower === 'walmart' && !hasWalmartSeller) return false;
                }

                if (filters.walmart.selected_badges.length > 0) {
                    const dealBadges = (data.badges || []).map(b => b.text);
                    const hasBadge = filters.walmart.selected_badges.some(selectedBadge => dealBadges.includes(selectedBadge));
                    if (!hasBadge) return false;
                }

                const excludedBrands = JSON.parse(localStorage.getItem('excluded_brands') || '[]');
                const brand = (data.brand || '').toLowerCase();
                if (excludedBrands.some(b => brand === b.toLowerCase())) return false;

                if (filters.shared.selected_categories.length > 0) {
                    const dealCategory = data.category || '';
                    if (!filters.shared.selected_categories.includes(dealCategory)) return false;
                }

                if (filters.walmart.min_offers >= 0 && (data.offers || 0) < filters.walmart.min_offers) return false;
                if (filters.walmart.max_offers >= 0 && (data.offers || 0) > filters.walmart.max_offers) return false;
                if (filters.walmart.min_reviews > 0 && (data.number_of_reviews || 0) < filters.walmart.min_reviews) return false;
                if (filters.walmart.min_rating > 0 && (data.average_rating || 0) < filters.walmart.min_rating) return false;

            } else if (deal.marketplace === 'ebay') {
                sellPrice = data.lowest_price || 0;
                const totalSale = sellPrice;
                let finalValueFee = totalSale <= 7500 ? totalSale * 0.136 : (7500 * 0.136) + ((totalSale - 7500) * 0.0235);
                const storeDiscount = { none: 0, basic: 0.066, premium: 0.088, anchor: 0.102, enterprise: 0.110 }[feeSettings.storeSubscription || 'none'] || 0;
                const topRatedDiscount = feeSettings.topRated ? 0.10 : 0;
                finalValueFee = finalValueFee * (1 - storeDiscount - topRatedDiscount);
                const perOrderFee = totalSale <= 10 ? 0.30 : 0.40;
                const paymentProcessingFee = (totalSale * 0.0235) + 0.30;
                const salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
                const packagingCost = feeSettings.packagingCost || 0;
                const otherCosts = feeSettings.otherCosts || 0;
                const totalFees = finalValueFee + perOrderFee + paymentProcessingFee + salesTax + packagingCost + otherCosts;
                profit = sellPrice - sourcePrice - totalFees;
                roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

                if (filters.ebay.min_offers >= 0 && (data.total_offers || 0) < filters.ebay.min_offers) return false;
                if (filters.ebay.max_offers >= 0 && (data.total_offers || 0) > filters.ebay.max_offers) return false;
                if (filters.ebay.min_est_sales > 0 && (data.estimated_monthly_sales || 0) < filters.ebay.min_est_sales) return false;

                if (filters.ebay.exclude_walmart_3p) {
                    const supplierLower = (deal.supplier_name || '').toLowerCase();
                    const titleLower = (deal.title || '').toLowerCase();
                    const hasWalmartSeller = titleLower.includes('(seller: walmart.com)') || titleLower.includes('(seller: walmart)');
                    if (supplierLower === 'walmart' && !hasWalmartSeller) return false;
                }
            }

            if (!deal.supplier_name) profit = 0;

            if (filters.min_sell_price > 0 && sellPrice < filters.min_sell_price) return false;
            if (filters.max_sell_price > 0 && sellPrice > filters.max_sell_price) return false;
            if (filters.profitable_only && profit <= 0) return false;
            if (filters.min_roi >= 0 && roi < filters.min_roi) return false;
            if (filters.max_roi >= 0 && roi > filters.max_roi) return false;
            if (filters.min_profit >= 0 && profit < filters.min_profit) return false;
            if (filters.max_profit >= 0 && profit > filters.max_profit) return false;

            return true;
        });

        // Group deals by marketplace product ID
        const grouped = {};
        result.forEach(deal => {
            const data = deal.marketplace_data || {};
            let productId = deal.marketplace === 'amazon' ? data.asin :
                deal.marketplace === 'walmart' ? data.item_id : deal.upc;
            const groupKey = productId ? `${deal.marketplace}_${productId}` : `standalone_${deal.id}`;

            if (!grouped[groupKey]) {
                grouped[groupKey] = { primaryDeal: deal, alternativeSources: [] };
            } else {
                if ((deal.source_price || 0) < (grouped[groupKey].primaryDeal.source_price || 0)) {
                    grouped[groupKey].alternativeSources.push(grouped[groupKey].primaryDeal);
                    grouped[groupKey].primaryDeal = deal;
                } else {
                    grouped[groupKey].alternativeSources.push(deal);
                }
            }
        });

        setFilteredDeals(Object.values(grouped));
    }, [favorites, filters, settingsUpdateTrigger]);

    // Build a map from deal id to favorite id for remove functionality
    const dealToFavoriteId = {};
    favorites.forEach(f => {
        if (f.deal_data) dealToFavoriteId[f.deal_data.id] = f.id;
    });

    const handleRemoveFavorite = async (dealId) => {
        const favoriteId = dealToFavoriteId[dealId];
        if (!favoriteId) return;
        if (!window.confirm('Remove this item from favorites?')) return;

        try {
            await authService.authenticatedFetch(`${API_BASE}/api/favorites/${favoriteId}`, {
                method: 'DELETE'
            });
            setFavorites(favorites.filter(f => f.id !== favoriteId));
        } catch (error) {
            console.error('Failed to remove favorite:', error);
            alert('Failed to remove from favorites');
        }
    };

    const toggleSelect = (dealId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(dealId)) next.delete(dealId);
            else next.add(dealId);
            return next;
        });
    };

    const selectAll = () => {
        setSelectedIds(new Set(filteredDeals.map(g => g.primaryDeal.id)));
    };

    const deselectAll = () => setSelectedIds(new Set());

    const handleBulkDelete = async (ids) => {
        const count = ids.size || ids.length;
        if (!window.confirm(`Delete ${count} favorite${count !== 1 ? 's' : ''}? This cannot be undone.`)) return;

        setBulkDeleting(true);
        const idsArray = [...ids];
        const BATCH_SIZE = 10;
        let failedCount = 0;
        const allDeletedFavIds = [];

        for (let i = 0; i < idsArray.length; i += BATCH_SIZE) {
            const batch = idsArray.slice(i, i + BATCH_SIZE);
            const results = await Promise.allSettled(
                batch.map(dealId => {
                    const favoriteId = dealToFavoriteId[dealId];
                    if (!favoriteId) return Promise.resolve();
                    return authService.authenticatedFetch(`${API_BASE}/api/favorites/${favoriteId}`, { method: 'DELETE' });
                })
            );
            failedCount += results.filter(r => r.status === 'rejected').length;
            const deletedFavIds = batch
                .filter((_, j) => results[j].status === 'fulfilled')
                .map(id => dealToFavoriteId[id])
                .filter(Boolean);
            allDeletedFavIds.push(...deletedFavIds);
            setFavorites(prev => prev.filter(f => !allDeletedFavIds.includes(f.id)));

            if (i + BATCH_SIZE < idsArray.length) {
                await new Promise(r => setTimeout(r, 5000));
            }
        }

        setSelectedIds(new Set());
        setBulkDeleting(false);
        if (failedCount) alert(`${failedCount} item(s) failed to delete.`);
    };

    const handleDeleteAll = () => {
        const allIds = new Set(filteredDeals.map(g => g.primaryDeal.id));
        handleBulkDelete(allIds);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <Heart size={32} className="text-red-500" fill="currentColor" />
                    <h1 className="text-3xl font-bold text-gray-800">My Favorites</h1>
                    <div className="ml-auto flex items-center gap-2">
                        {bulkMode ? (
                            <>
                                <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
                                <button onClick={selectAll} className="px-3 py-1.5 text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 transition">
                                    Select All
                                </button>
                                <button onClick={deselectAll} className="px-3 py-1.5 text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition">
                                    Deselect All
                                </button>
                                <button
                                    onClick={() => handleBulkDelete(selectedIds)}
                                    disabled={selectedIds.size === 0 || bulkDeleting}
                                    className="px-3 py-1.5 text-xs font-semibold bg-red-500 text-white rounded-lg hover:bg-red-600 transition disabled:opacity-40 flex items-center gap-1"
                                >
                                    {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                    Delete Selected
                                </button>
                                <button
                                    onClick={handleDeleteAll}
                                    disabled={filteredDeals.length === 0 || bulkDeleting}
                                    className="px-3 py-1.5 text-xs font-semibold bg-red-700 text-white rounded-lg hover:bg-red-800 transition disabled:opacity-40 flex items-center gap-1"
                                >
                                    <Trash2 size={12} /> Delete All
                                </button>
                                <button
                                    onClick={() => { setBulkMode(false); setSelectedIds(new Set()); }}
                                    className="px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <span className="text-sm text-gray-600">
                                    {filteredDeals.length} of {favorites.length} {favorites.length === 1 ? 'favorite' : 'favorites'}
                                </span>
                                {favorites.length > 0 && (
                                    <button
                                        onClick={() => setBulkMode(true)}
                                        className="px-3 py-1.5 text-xs font-semibold bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition flex items-center gap-1"
                                    >
                                        <Trash2 size={12} /> Bulk Delete
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Advanced Filters */}
                <AdvancedFilters
                    filters={filters}
                    setFilters={setFilters}
                    marketplace={filters.marketplace}
                    exportToCSV={() => exportToCSV(filteredDeals, filters)}
                    initialFilters={INITIAL_FILTERS}
                    walmartBadges={getAllWalmartBadges()}
                    allSuppliers={getAllSuppliers()}
                    allCategories={getAllCategories()}
                    onSettingsChange={() => setSettingsUpdateTrigger(prev => prev + 1)}
                    isCompactView={isCompactView}
                    setIsCompactView={setIsCompactView}
                    afRender={afRender}
                    setAFRender={setAFRender}
                />
            </div>

            {/* Favorites List */}
            {filteredDeals.length === 0 ? (
                <div className="text-center py-12">
                    <Heart size={64} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        {favorites.length === 0 ? 'No favorites yet' : 'No favorites match current filters'}
                    </h3>
                    <p className="text-gray-500">
                        {favorites.length === 0
                            ? 'Start adding deals to your favorites by clicking the heart icon!'
                            : 'Try adjusting your filters to see more results.'}
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredDeals.map(groupedDeal => {
                        const dealId = groupedDeal.primaryDeal.id;
                        const isSelected = selectedIds.has(dealId);
                        return (
                            <div key={dealId} className={`flex items-stretch gap-2 ${bulkMode && isSelected ? 'ring-2 ring-red-300 rounded-lg' : ''}`}>
                                {bulkMode && (
                                    <button
                                        onClick={() => toggleSelect(dealId)}
                                        className="flex-shrink-0 flex items-center justify-center w-10 transition-colors"
                                    >
                                        {isSelected
                                            ? <CheckSquare size={20} className="text-red-500" />
                                            : <Square size={20} className="text-gray-300 hover:text-gray-500" />
                                        }
                                    </button>
                                )}
                                <div className="flex-1 min-w-0">
                                    {isCompactView ? (
                                        <MinimizedDealCard
                                            deal={groupedDeal.primaryDeal}
                                            alternativeSources={groupedDeal.alternativeSources}
                                            couponAmount={filters.coupon_amount}
                                            fulfillmentType={groupedDeal.primaryDeal.marketplace === 'amazon' ? filters.amazon.fulfillment : groupedDeal.primaryDeal.marketplace === 'walmart' ? filters.walmart.fulfillment : null}
                                            onSettingsChange={() => setSettingsUpdateTrigger(prev => prev + 1)}
                                            amazonLinked={amazonLinked}
                                        />
                                    ) : (
                                        <DealCard
                                            deal={groupedDeal.primaryDeal}
                                            alternativeSources={groupedDeal.alternativeSources}
                                            couponAmount={filters.coupon_amount}
                                            fulfillmentType={groupedDeal.primaryDeal.marketplace === 'amazon' ? filters.amazon.fulfillment : groupedDeal.primaryDeal.marketplace === 'walmart' ? filters.walmart.fulfillment : null}
                                            onSettingsChange={() => setSettingsUpdateTrigger(prev => prev + 1)}
                                            amazonLinked={amazonLinked}
                                        />
                                    )}
                                </div>
                                {!bulkMode && (
                                    <button
                                        onClick={() => handleRemoveFavorite(dealId)}
                                        className="flex-shrink-0 flex items-center justify-center w-10 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow transition-colors"
                                        title="Remove from favorites"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Favorites;
